import React, { useState } from 'react';
import { Card, Modal, Button, Badge } from 'react-bootstrap';
import { CircularProgress } from '@mui/material';
import { toast } from 'react-toastify';
import { Shield, Monitor, Eye, Copy, AlertTriangle, CheckCircle, XCircle, Clock, Code, Coffee, Camera } from 'react-feather';
import { updateCodeSandboxProgress } from 'ServerAPI/serverAPI';

// Parse output lines into per-test-case pass/fail
const parseTestResults = (output = [], testCases = []) => {
    const statusMap = {};
    let currentIdx = null;

    for (const line of output) {
        const runMatch = line.match(/test case (\d+)/i);
        if (runMatch) {
            currentIdx = parseInt(runMatch[1]) - 1;
        }
        if (currentIdx !== null) {
            if (line.includes('✅') || line.toLowerCase().includes('passed')) {
                statusMap[currentIdx] = true;
            } else if (line.includes('❌') || line.toLowerCase().includes('failed')) {
                statusMap[currentIdx] = false;
            }
        }
    }

    return testCases.map((tc, i) => ({
        ...tc,
        index: i,
        passed: statusMap[i] ?? null,
    }));
};

const formatDuration = (seconds) => {
    if (seconds === undefined || seconds === null || isNaN(seconds)) return '—';
    seconds = Math.round(seconds);
    if (seconds < 60) return `${seconds}s`;
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = seconds % 60;
    return h > 0 ? `${h}h ${m}m ${s}s` : `${m}m ${s}s`;
};

const VIOLATION_ICONS = {
    fullscreenExit: Monitor,
    tabSwitch: Eye,
    blockedShortcut: AlertTriangle,
    copyAttempt: Copy,
};

// Compute initial earned marks from raw userData (called once on mount via useState initializer)
const computeInitialMarks = (ud) => {
    const mptc = ud?.marksPerTestCase || 1;
    const tc = ud?.testCases || [];
    const out = ud?.output || [];
    const tr = parseTestResults(out, tc);
    const spc = ud?.passedTestCases;
    const pc = spc ?? tr.filter(r => r.passed === true).length;
    return ud?.earnedMarks ?? mptc * pc;
};

const CodeReviewDisplay = ({ show, handleClose, userData, courseId, userId, sectionId: sectionIdProp }) => {
    // Initialized once from props on mount — parent uses `key` to reset per submission
    const [overriddenEarnedMarks, setOverriddenEarnedMarks] = useState(() => String(computeInitialMarks(userData)));
    const [teacherFeedback, setTeacherFeedback] = useState(() => userData?.teacherFeedback || '');
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    if (!show || !userData) return null;

    const {
        contentTitle,
        problemStatement,
        language,
        codeSnippet,
        output = [],
        testCases = [],
        codeProctoringData,
        submitReason,
        submittedAt,
        startedAt,
        marksPerTestCase = 1,
        totalMarks,
        earnedMarks: savedEarnedMarks,
        passedTestCases: savedPassedCount,
        contentId,
        sectionId: sectionIdFromData,
    } = userData;

    const sectionId = sectionIdFromData || sectionIdProp;

    const violations = codeProctoringData?.violations || [];
    const pasteEvents = codeProctoringData?.pasteEvents || [];
    const idleEvents = codeProctoringData?.idleEvents || [];
    const codeSnapshots = codeProctoringData?.codeSnapshots || [];
    const totalViolations = violations.reduce((s, v) => s + v.count, 0);
    const totalIdleSeconds = idleEvents.reduce((s, e) => s + (e.idleDurationSeconds || 0), 0);
    const timeSpentSeconds = (startedAt && submittedAt)
        ? (new Date(submittedAt) - new Date(startedAt)) / 1000
        : null;

    const testResults = parseTestResults(output, testCases);
    const passedCount = savedPassedCount ?? testResults.filter(r => r.passed === true).length;
    const computedTotalMarks = totalMarks ?? marksPerTestCase * testCases.length;
    const computedEarnedMarks = savedEarnedMarks ?? marksPerTestCase * passedCount;

    const displayEarned = parseFloat(overriddenEarnedMarks) || 0;
    const allPassed = passedCount === testCases.length && testCases.length > 0;

    const handleSave = async () => {
        setShowConfirmModal(false);
        setSubmitting(true);
        try {
            const payload = {
                courseId,
                sectionId,
                contentId,
                userId,
                earnedMarks: displayEarned,
                totalMarks: computedTotalMarks,
                teacher_reviewed: true,
                teacherFeedback: teacherFeedback.trim(),
            };
            const response = await updateCodeSandboxProgress(payload);
            if (response?.status === 200 || response?.data) {
                toast.success('Marks updated successfully!');
                handleClose();
            } else {
                toast.error('Failed to update marks. Please try again.');
            }
        } catch (err) {
            toast.error('Error saving marks: ' + err.message);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <>
            {/* ── Backdrop + panel ────────────────────────────────────────────── */}
            <div style={{
                position: 'fixed', inset: 0,
                background: 'rgba(0,0,0,0.5)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                zIndex: 30000,
            }}>
                <div style={{
                    background: 'white', width: '90%', maxWidth: '95vw', maxHeight: '95vh',
                    borderRadius: '8px', boxShadow: '0 4px 10px rgba(0,0,0,0.3)',
                    overflow: 'hidden', display: 'flex', flexDirection: 'column',
                }}>

                    {/* ── Header ─────────────────────────────────────────────── */}
                    <div style={{
                        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                        padding: '16px', background: 'rgba(34,75,153,1)', color: 'white', flexShrink: 0,
                    }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <Code size={18} />
                            <h5 style={{ margin: 0, color: 'white' }}>
                                Code Submission Review — {contentTitle || 'Coding Assignment'}
                            </h5>
                        </div>
                        <button onClick={handleClose} style={{ background: 'transparent', border: 'none', color: 'white', fontSize: '20px', cursor: 'pointer' }}>✖</button>
                    </div>

                    {/* ── Scrollable body ─────────────────────────────────────── */}
                    <div style={{ padding: '20px', overflowY: 'auto', flex: 1 }}>

                        {/* ── Summary bar ─────────────────────────────────────── */}
                        <div style={{
                            display: 'flex', flexWrap: 'wrap', gap: '24px', padding: '14px 18px',
                            background: '#f0f4f8', borderRadius: '8px', marginBottom: '16px',
                            border: '1px solid #d1d9e6', fontSize: '0.85rem',
                        }}>
                            <div>
                                <div style={{ color: '#6c757d', marginBottom: '2px' }}>Language</div>
                                <div style={{ fontWeight: '600', color: '#224B99' }}>{language || '—'}</div>
                            </div>
                            <div style={{ borderLeft: '1px solid #dee2e6', paddingLeft: '24px' }}>
                                <div style={{ color: '#6c757d', marginBottom: '2px' }}>Test Cases</div>
                                <div style={{ fontWeight: '600' }}>
                                    <span style={{ color: passedCount === testCases.length ? '#28a745' : '#dc3545', fontWeight: '700' }}>{passedCount}</span>
                                    <span style={{ color: '#6c757d' }}> / {testCases.length} passed</span>
                                </div>
                            </div>
                            <div style={{ borderLeft: '1px solid #dee2e6', paddingLeft: '24px' }}>
                                <div style={{ color: '#6c757d', marginBottom: '2px' }}>Marks per Test Case</div>
                                <div style={{ fontWeight: '600' }}>{marksPerTestCase}</div>
                            </div>
                            <div style={{ borderLeft: '1px solid #dee2e6', paddingLeft: '24px' }}>
                                <div style={{ color: '#6c757d', marginBottom: '2px' }}>Submission</div>
                                <div style={{ fontWeight: '600' }}>{submitReason === 'timer' ? 'Timer Expired' : 'Manual'}</div>
                            </div>
                            {submittedAt && (
                                <div style={{ borderLeft: '1px solid #dee2e6', paddingLeft: '24px' }}>
                                    <div style={{ color: '#6c757d', marginBottom: '2px' }}>Submitted At</div>
                                    <div style={{ fontWeight: '600' }}>{new Date(submittedAt).toLocaleString()}</div>
                                </div>
                            )}
                            {timeSpentSeconds !== null && (
                                <div style={{ borderLeft: '1px solid #dee2e6', paddingLeft: '24px' }}>
                                    <div style={{ color: '#6c757d', marginBottom: '2px' }}>Time Spent</div>
                                    <div style={{ fontWeight: '600', color: '#224B99' }}>{formatDuration(timeSpentSeconds)}</div>
                                </div>
                            )}
                            {/* Score */}
                            <div style={{ marginLeft: 'auto', textAlign: 'right' }}>
                                <div style={{ color: '#6c757d', marginBottom: '2px' }}>Score</div>
                                <div style={{ fontSize: '1.3rem', fontWeight: '800', color: allPassed ? '#28a745' : '#dc3545' }}>
                                    {displayEarned} / {computedTotalMarks}
                                    {computedTotalMarks > 0 && (
                                        <span style={{ fontSize: '0.78rem', color: '#6c757d', marginLeft: '6px', fontWeight: '500' }}>
                                            ({((displayEarned / computedTotalMarks) * 100).toFixed(1)}%)
                                        </span>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* ── Proctoring Report ───────────────────────────────── */}
                        {violations.length > 0 && (
                            <div style={{ padding: '16px', background: '#fff', borderRadius: '8px', marginBottom: '16px', border: '1px solid #dee2e6', boxShadow: '0 2px 4px rgba(0,0,0,0.04)' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#224B99', fontWeight: '700', fontSize: '0.9rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                        <Shield size={16} /> Proctoring Report
                                    </div>
                                    <div style={{
                                        background: totalViolations === 0 ? '#d4edda' : '#fff3cd',
                                        color: totalViolations === 0 ? '#155724' : '#856404',
                                        padding: '4px 14px', borderRadius: '20px', fontSize: '0.8rem', fontWeight: '600',
                                    }}>
                                        {totalViolations === 0 ? '✓ No Violations' : `${totalViolations} Total Warning${totalViolations > 1 ? 's' : ''}`}
                                    </div>
                                </div>
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '10px', marginBottom: pasteEvents.length > 0 ? '12px' : 0 }}>
                                    {violations.map(log => {
                                        const Icon = VIOLATION_ICONS[log.type] || Shield;
                                        const isSevere = log.count >= 3;
                                        const isViolated = log.count > 0;
                                        const bg = isSevere ? '#fff5f5' : isViolated ? '#fffbf0' : '#f0fff4';
                                        const borderColor = isSevere ? '#f5c6cb' : isViolated ? '#ffd97d' : '#c3e6cb';
                                        const countColor = isSevere ? '#dc3545' : isViolated ? '#d97706' : '#28a745';
                                        const statusLabel = isSevere ? 'HIGH RISK' : isViolated ? 'WARNING' : 'CLEAN';
                                        return (
                                            <div key={log.type} style={{ background: bg, border: `1px solid ${borderColor}`, borderRadius: '8px', padding: '14px 12px', textAlign: 'center' }}>
                                                <div style={{ marginBottom: '6px', color: countColor }}><Icon size={20} /></div>
                                                <div style={{ fontSize: '0.72rem', color: '#6c757d', marginBottom: '8px', lineHeight: '1.35', minHeight: '30px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                    {log.label}
                                                </div>
                                                <div style={{ fontSize: '2rem', fontWeight: '800', color: countColor, lineHeight: '1' }}>{log.count}</div>
                                                <div style={{ marginTop: '8px', fontSize: '0.68rem', fontWeight: '700', letterSpacing: '0.5px', color: countColor }}>{statusLabel}</div>
                                            </div>
                                        );
                                    })}
                                </div>
                                {pasteEvents.length > 0 && (
                                    <div style={{ padding: '10px 14px', background: '#fffbf0', border: '1px solid #ffd97d', borderRadius: '6px', fontSize: '0.83rem', marginBottom: idleEvents.length > 0 ? '10px' : 0 }}>
                                        <div style={{ fontWeight: '700', color: '#856404', marginBottom: '6px' }}>📋 Paste Events ({pasteEvents.length})</div>
                                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px 12px', maxHeight: '70px', overflowY: 'auto' }}>
                                            {pasteEvents.map((e, i) => (
                                                <span key={i} style={{ display: 'inline-flex', alignItems: 'center', color: '#6c757d', background: '#fff', border: '1px solid #ffe7a8', borderRadius: '4px', padding: '2px 6px', fontSize: '0.76rem' }}>
                                                    <Clock size={11} style={{ marginRight: '3px' }} />
                                                    {new Date(e.timestamp).toLocaleTimeString()}
                                                </span>
                                            ))}
                                        </div>
                                    </div>
                                )}
                                {idleEvents.length > 0 && (
                                    <div style={{ padding: '10px 14px', background: '#fff5f5', border: '1px solid #f5c6cb', borderRadius: '6px', fontSize: '0.83rem' }}>
                                        <div style={{ fontWeight: '700', color: '#721c24', marginBottom: '6px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                            <Coffee size={13} /> Idle Periods ({idleEvents.length}) — total {formatDuration(totalIdleSeconds)}
                                        </div>
                                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px 12px', maxHeight: '70px', overflowY: 'auto' }}>
                                            {idleEvents.map((e, i) => (
                                                <span key={i} style={{ display: 'inline-flex', alignItems: 'center', color: '#721c24', background: '#fff', border: '1px solid #f5c6cb', borderRadius: '4px', padding: '2px 6px', fontSize: '0.76rem' }}>
                                                    <Clock size={11} style={{ marginRight: '3px' }} />
                                                    {new Date(e.timestamp).toLocaleTimeString()} · idle {formatDuration(e.idleDurationSeconds)}
                                                </span>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* ── Code Snapshots ──────────────────────────────────── */}
                        {codeSnapshots.length > 0 && (
                            <div style={{ padding: '16px', background: '#fff', borderRadius: '8px', marginBottom: '16px', border: '1px solid #dee2e6', boxShadow: '0 2px 4px rgba(0,0,0,0.04)' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#224B99', fontWeight: '700', fontSize: '0.9rem', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '12px' }}>
                                    <Camera size={16} /> Code Snapshots ({codeSnapshots.length})
                                </div>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', maxHeight: '260px', overflowY: 'auto' }}>
                                    {codeSnapshots.map((snap, i) => (
                                        <div key={i} style={{ border: '1px solid #e5e7eb', borderRadius: '6px', overflow: 'hidden' }}>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 10px', background: '#f8f9fa', fontSize: '0.76rem', color: '#6c757d' }}>
                                                <span style={{ fontWeight: '600', color: '#374151' }}>Snapshot {i + 1} — {new Date(snap.timestamp).toLocaleTimeString()}</span>
                                                <span>{snap.charCount} chars</span>
                                            </div>
                                            <pre style={{
                                                margin: 0, padding: '10px', background: '#0f172a', color: '#94a3b8',
                                                fontFamily: '"Fira Code", "Cascadia Code", monospace', fontSize: '0.76rem',
                                                lineHeight: '1.5', overflowX: 'auto', maxHeight: '110px', overflowY: 'auto',
                                                whiteSpace: 'pre-wrap',
                                            }}>
                                                {snap.preview || '—'}
                                            </pre>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 340px', gap: '16px', alignItems: 'start' }}>

                            {/* ── Left: problem + code ─────────────────────────── */}
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>

                                {/* Problem statement */}
                                {problemStatement && (
                                    <Card style={{ border: '1px solid #dee2e6' }}>
                                        <Card.Body>
                                            <h6 style={{ color: '#224B99', fontWeight: '700', marginBottom: '8px', textTransform: 'uppercase', fontSize: '0.78rem', letterSpacing: '0.5px' }}>Problem Statement</h6>
                                            <p style={{ margin: 0, color: '#374151', lineHeight: '1.6', fontSize: '0.92rem' }}>{problemStatement}</p>
                                        </Card.Body>
                                    </Card>
                                )}

                                {/* Submitted code */}
                                <Card style={{ border: '1px solid #dee2e6' }}>
                                    <Card.Header style={{ background: '#1e293b', color: 'white', fontWeight: '600', fontSize: '0.85rem', padding: '10px 16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        <Code size={14} /> Submitted Code — {language}
                                    </Card.Header>
                                    <Card.Body style={{ padding: 0 }}>
                                        <pre style={{
                                            margin: 0, padding: '16px', background: '#0f172a', color: '#e2e8f0',
                                            fontFamily: '"Fira Code", "Cascadia Code", monospace', fontSize: '0.88rem',
                                            lineHeight: '1.6', overflowX: 'auto', maxHeight: '360px', overflowY: 'auto',
                                            borderRadius: '0 0 6px 6px',
                                        }}>
                                            {codeSnippet || '— No code submitted —'}
                                        </pre>
                                    </Card.Body>
                                </Card>
                            </div>

                            {/* ── Right: test case results + marks editor ──────── */}
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>

                                {/* Marks editor */}
                                <Card style={{ border: '1px solid #dee2e6', boxShadow: '0 2px 6px rgba(0,0,0,0.06)' }}>
                                    <Card.Body>
                                        <h6 style={{ color: '#224B99', fontWeight: '700', marginBottom: '14px', textTransform: 'uppercase', fontSize: '0.78rem', letterSpacing: '0.5px' }}>
                                            Marks Override
                                        </h6>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
                                            <div style={{ flex: 1 }}>
                                                <div style={{ fontSize: '0.75rem', color: '#6c757d', marginBottom: '4px' }}>Earned Marks</div>
                                                <input
                                                    type="number"
                                                    step="0.5"
                                                    min={0}
                                                    max={computedTotalMarks}
                                                    value={overriddenEarnedMarks}
                                                    onChange={e => setOverriddenEarnedMarks(e.target.value)}
                                                    style={{
                                                        width: '100%', padding: '8px 10px', borderRadius: '6px',
                                                        border: parseFloat(overriddenEarnedMarks) !== computedEarnedMarks ? '2px solid #224B99' : '1px solid #dee2e6',
                                                        fontSize: '1rem', fontWeight: '700', color: '#224B99',
                                                        outline: 'none',
                                                    }}
                                                />
                                            </div>
                                            <div style={{ paddingTop: '18px', color: '#6c757d', fontWeight: '600' }}>/ {computedTotalMarks}</div>
                                        </div>
                                        {parseFloat(overriddenEarnedMarks) !== computedEarnedMarks && (
                                            <div style={{ fontSize: '0.78rem', color: '#856404', background: '#fff3cd', padding: '6px 10px', borderRadius: '4px' }}>
                                                ✏️ Overriding auto-calculated marks ({computedEarnedMarks})
                                            </div>
                                        )}
                                    </Card.Body>
                                </Card>

                                {/* Teacher feedback */}
                                <Card style={{ border: '1px solid #dee2e6', boxShadow: '0 2px 6px rgba(0,0,0,0.06)' }}>
                                    <Card.Body>
                                        <h6 style={{ color: '#224B99', fontWeight: '700', marginBottom: '10px', textTransform: 'uppercase', fontSize: '0.78rem', letterSpacing: '0.5px' }}>
                                            Teacher Feedback
                                        </h6>
                                        <textarea
                                            rows={4}
                                            placeholder="Write feedback for the student..."
                                            value={teacherFeedback}
                                            onChange={e => setTeacherFeedback(e.target.value)}
                                            style={{
                                                width: '100%', padding: '8px 10px', borderRadius: '6px',
                                                border: teacherFeedback.trim() ? '2px solid #224B99' : '1px solid #dee2e6',
                                                fontSize: '0.88rem', lineHeight: '1.5', color: '#374151',
                                                resize: 'vertical', outline: 'none', fontFamily: 'inherit',
                                            }}
                                        />
                                        <div style={{ fontSize: '0.72rem', color: '#6c757d', marginTop: '4px', textAlign: 'right' }}>
                                            {teacherFeedback.length} characters
                                        </div>
                                    </Card.Body>
                                </Card>

                                {/* Per-test-case results */}
                                <Card style={{ border: '1px solid #dee2e6' }}>
                                    <Card.Header style={{ background: '#f8f9fa', fontWeight: '700', fontSize: '0.82rem', color: '#374151', padding: '10px 14px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                        Test Case Results
                                    </Card.Header>
                                    <Card.Body style={{ padding: '12px', maxHeight: '420px', overflowY: 'auto' }}>
                                        {testResults.length === 0 ? (
                                            <p style={{ color: '#6c757d', fontSize: '0.85rem', margin: 0, textAlign: 'center' }}>No test cases configured</p>
                                        ) : (
                                            testResults.map((tc, i) => {
                                                const passed = tc.passed;
                                                const earned = passed === true ? marksPerTestCase : 0;
                                                const bg = passed === true ? '#f0fff4' : passed === false ? '#fff5f5' : '#f8f9fa';
                                                const borderColor = passed === true ? '#c3e6cb' : passed === false ? '#f5c6cb' : '#dee2e6';
                                                const textColor = passed === true ? '#155724' : passed === false ? '#721c24' : '#6c757d';
                                                return (
                                                    <div key={i} style={{
                                                        background: bg, border: `1px solid ${borderColor}`,
                                                        borderRadius: '8px', padding: '10px 12px', marginBottom: '8px',
                                                    }}>
                                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                                                            <span style={{ fontWeight: '700', fontSize: '0.82rem', color: '#374151' }}>Test Case {i + 1}</span>
                                                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                                {passed === true && <CheckCircle size={14} color="#28a745" />}
                                                                {passed === false && <XCircle size={14} color="#dc3545" />}
                                                                <span style={{ fontSize: '0.75rem', fontWeight: '700', color: textColor }}>
                                                                    {passed === true ? 'PASSED' : passed === false ? 'FAILED' : 'N/A'}
                                                                </span>
                                                                <Badge style={{
                                                                    background: passed === true ? '#28a745' : passed === false ? '#dc3545' : '#6c757d',
                                                                    fontSize: '0.7rem', padding: '3px 7px',
                                                                }}>
                                                                    {earned} / {marksPerTestCase} marks
                                                                </Badge>
                                                            </div>
                                                        </div>
                                                        <div style={{ fontSize: '0.75rem', color: '#6c757d', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px' }}>
                                                            <div><span style={{ fontWeight: '600' }}>Input:</span> <code style={{ fontSize: '0.72rem' }}>{tc.testInput || '—'}</code></div>
                                                            <div><span style={{ fontWeight: '600' }}>Expected:</span> <code style={{ fontSize: '0.72rem' }}>{tc.expectedOutput || '—'}</code></div>
                                                        </div>
                                                    </div>
                                                );
                                            })
                                        )}
                                        {/* Summary row */}
                                        {testResults.length > 0 && (
                                            <div style={{ borderTop: '1px solid #dee2e6', paddingTop: '10px', marginTop: '4px', display: 'flex', justifyContent: 'space-between', fontSize: '0.83rem', fontWeight: '700' }}>
                                                <span style={{ color: '#374151' }}>Total</span>
                                                <span style={{ color: allPassed ? '#28a745' : '#dc3545' }}>
                                                    {displayEarned} / {computedTotalMarks} marks
                                                </span>
                                            </div>
                                        )}
                                    </Card.Body>
                                </Card>
                            </div>
                        </div>
                    </div>

                    {/* ── Footer ─────────────────────────────────────────────── */}
                    <div style={{
                        padding: '12px 16px', borderTop: '1px solid #dee2e6',
                        display: 'flex', justifyContent: 'flex-end',
                        background: '#f8f9fa', flexShrink: 0,
                    }}>
                        <button
                            onClick={() => setShowConfirmModal(true)}
                            disabled={submitting}
                            style={{
                                background: '#224B99', color: 'white', border: 'none',
                                borderRadius: '6px', padding: '10px 32px',
                                fontSize: '0.95rem', fontWeight: '600',
                                cursor: submitting ? 'not-allowed' : 'pointer',
                                opacity: submitting ? 0.7 : 1,
                                display: 'flex', alignItems: 'center', gap: '8px',
                            }}
                        >
                            {submitting ? <><CircularProgress size={16} style={{ color: 'white' }} /> Saving...</> : 'Save Marks'}
                        </button>
                    </div>
                </div>
            </div>

            {/* ── Confirm modal ───────────────────────────────────────────────── */}
            <Modal show={showConfirmModal} onHide={() => setShowConfirmModal(false)} centered style={{ zIndex: 40000 }}
                contentClassName="border border-2 border-primary shadow-lg rounded-3">
                <Modal.Header closeButton>
                    <Modal.Title style={{ fontSize: '1rem', fontWeight: '700' }}>Confirm Mark Submission</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p style={{ marginBottom: '8px' }}>Are you sure you want to save these marks?</p>
                    <div style={{ padding: '12px', background: '#f0f4f8', borderRadius: '6px', fontSize: '0.85rem' }}>
                        <div><strong>Earned Marks:</strong> {displayEarned} / {computedTotalMarks}</div>
                        <div><strong>Test Cases Passed:</strong> {passedCount} / {testCases.length}</div>
                        {parseFloat(overriddenEarnedMarks) !== computedEarnedMarks && (
                            <div style={{ marginTop: '6px', color: '#856404' }}>
                                ✏️ You have manually overridden the auto-calculated marks ({computedEarnedMarks}).
                            </div>
                        )}
                    </div>
                    <p style={{ color: '#dc3545', fontSize: '0.875rem', marginTop: '10px', marginBottom: 0 }}>
                        ⚠️ <strong>This action cannot be undone.</strong>
                    </p>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowConfirmModal(false)}>Cancel</Button>
                    <Button onClick={handleSave} style={{ background: '#224B99', border: 'none', fontWeight: '600' }}>
                        Yes, Save
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    );
};

export default CodeReviewDisplay;
